import React, { Component } from 'react'
import { Table, Icon, Button } from 'antd'
import { TableAction } from 'components/TableAction'
import { MODAL_TYPES } from 'components/ModalFactory'
import { ModalFactory } from 'components/ModalFactory'
import { PermissionService } from 'services/PermissionService'
import { Permissions } from 'permissions'
import { BaseRequest, ConfigurationRequest } from 'models/request/Index'
import { addConfiguration, deleteConfiguration, editConfiguration, getConfiguration } from 'services/CeberusApiService'
import { TenantService } from 'connection/TenantService'
import { JsonDocument, Tenant } from 'models/Blip/Index'
import { SearchTable } from 'components/SearchTable'
import { PushNotification } from 'utils/PushNotification'

const { Column } = Table;

const handleSearch = (selectedKeys: string, confirm: Function) => () : void => {
  confirm();
}
  
const handleReset = (clearFilters: Function) => () : void => {
  clearFilters();
}

export class Configurations extends Component<BaseRequest> {

  formRef: any;
  tenantSelected: Tenant;

  state = {
    isLoading: false,
    isConfigModalVisible: false,
    totalItems: 0,
    tableRows: [],
    searchingIdentifier: '',
    searchText: '',
    currentModal: '',
    editingRecord: {},
    canAdd: PermissionService.hasPermission(Permissions.Application_Configurations__Add),
    canEdit: PermissionService.hasPermission(Permissions.Application_Configurations__Edit),
    canRemove: PermissionService.hasPermission(Permissions.Application_Configurations__Remove),
  }

  componentDidMount() {
    this.tenantSelected = TenantService.getSelectedTenant();
    this.onSearch(this.props.BotIdentifier);
  }

  setComponentLoading(isLoading: boolean) : void {
    this.setState({ isLoading })
  }

  handleModalVisibility = (type: string, visibility: boolean) : void => {
    switch(type) {
      case MODAL_TYPES.AddConfiguration :
        if (!this.state.canAdd) {
          return;
        }
      case MODAL_TYPES.EditConfiguration :
        this.setState({
          isConfigModalVisible: visibility,
          currentModal: type,
        })

        if (!visibility) {
          this.setState({ editingRecord: {} })
        }
        break
      default:
        throw new Error('Unrecognized modal type')
    }
  }

  handleSaveConfig = () : void => {
    const form = this.formRef.props.form;

    form.validateFields(async (err, values) => {
      if (err) {
        return
      }

      try {
        const { owner, name, value } = values;

        const request = new ConfigurationRequest(
          new JsonDocument(name, value), 
          owner, 
          this.state.searchingIdentifier,
          this.state.searchingIdentifier,
          this.tenantSelected.name
        );

        if(this.state.currentModal == MODAL_TYPES.AddConfiguration){
          const response = await addConfiguration(request);
          PushNotification.success(response);
        }else{
          const response = await editConfiguration(request);
          PushNotification.success(response);
        }

        form.resetFields();
        this.setState({ isConfigModalVisible: false, editingRecord: {} });
        this.bindTableRowsByIdentifier(this.state.searchingIdentifier);
      } catch(error) {
        PushNotification.httpError(error);
      }
    })
  }

  saveFormRef = (formRef: any) : void => {
    this.formRef = formRef;
  }

  onSearch = async (identifier: string) : Promise<void> => {
    try {
      this.setComponentLoading(true);
      await this.bindTableRowsByIdentifier(identifier);
    } catch (error) {
      PushNotification.httpError(error);
    } finally {
      this.setComponentLoading(false);
    }
  }

  async bindTableRowsByIdentifier(identifier: string) : Promise<void> {
    const request = new ConfigurationRequest();
    request.BotIdentifier = identifier;
    request.ClusterName = this.tenantSelected.name;
    const response = await getConfiguration(request);

    const items = response.map(item => ({
      owner: `${item.owner.Name}@${item.owner.Domain}`,
      caller: `${item.caller.Name}@${item.caller.Domain}`,
      value: item.value,
      name: item.name
    }))

    const totalItems = items.length;
    this.setState(prevState => ({
      ...prevState,
      totalItems,
      searchingIdentifier: identifier,
      tableRows: items.map((rows: any, index: number) => ({
        ...rows,
        key: index
      }))
    }))
  }

  onRemove = async ({ caller: identifier, owner, name }) : Promise<void> => {
    if (!this.state.canRemove) {
      return;
    }
    try {
      this.setComponentLoading(true);
      const response = await deleteConfiguration(
        new ConfigurationRequest(
          new JsonDocument(name, String()), 
          owner, 
          identifier,
          identifier,
          this.tenantSelected.name
        ));
      PushNotification.success(response);
      this.bindTableRowsByIdentifier(identifier);
    } catch (error) {
      PushNotification.httpError(error);
    } finally {
      this.setComponentLoading(false)
    }
  }

  handleOnEditConfig = (record: any) : void => {
    if (this.state.canEdit) {
      this.setState({ editingRecord: record })
      this.handleModalVisibility(MODAL_TYPES.EditConfiguration, true)
    }
  }

  filterByRecord = (record: any, value: any) : boolean => {
    const lowerCasedValue = value.toLowerCase()

    return record.owner.toLowerCase().includes(lowerCasedValue)
      || record.name.toLowerCase().includes(lowerCasedValue)
      || record.value.toLowerCase().includes(lowerCasedValue)
  }

  render() {
    let searchInput;

    return (
      <div className="">
        <div>
          <Button
            type="primary"
            onClick={() => this.handleModalVisibility(MODAL_TYPES.AddConfiguration, true)}
            disabled={!this.state.canAdd} >
            <Icon type="plus-circle" /> Add configuration
          </Button>

          <ModalFactory
            wrappedComponentRef={this.saveFormRef}
            visible={this.state.isConfigModalVisible}
            onCancel={() => this.handleModalVisibility(MODAL_TYPES.AddConfiguration, false)}
            onOk={this.handleSaveConfig}
            type={this.state.currentModal}
            record={this.state.editingRecord}
            maskClosable={false}
          />
        </div>
        <div className="tr mb3">Total: {this.state.totalItems}</div>
        <Table
          dataSource={this.state.tableRows}
          loading={this.state.isLoading}
          expandedRowRender={record => (
            <p style={{ margin: 0, wordBreak: 'break-word' }}>{record.value}</p>
          )}
        >
          <Column 
            title="Owner" 
            dataIndex="owner" 
            key="owner"
            onFilter={(value, record) => this.filterByRecord(record, value)} 
            filterIcon={(filtered) => <Icon type="search" style={{ color: filtered ? '#108ee9' : '#aaa' }} />}
            filterDropdown={({
              setSelectedKeys, selectedKeys, confirm, clearFilters,
            }) => (
              <SearchTable
                onSetRef={e => (searchInput = e)}
                onSetSelectedKeys={setSelectedKeys}
                selectedKeys={selectedKeys}
                confirm={confirm}
                clearFilters={clearFilters}
                onSearch={handleSearch}
                onReset={handleReset}
              />)}/>
          <Column title="Name" dataIndex="name" key="name" />
          <Column 
            title="Value"
            dataIndex="value" 
            key="value"
            render={value => <p style={{ wordBreak: 'break-word' }}> {(value.length > 100 ? `${value.substring(0, 100)}...` : value)}</p>} 
          />
          <Column title="Allowed Actions" 
            render={(_, record: any) => (
              <TableAction
                onEdit={this.state.canEdit ? () => this.handleOnEditConfig(record) : undefined}
                onRemove={this.state.canRemove ? () => this.onRemove(record): undefined} />
            )}/>
        </Table>
      </div>
    )
  }
}
